/**
 * modules/AllModules.jsx
 * 8 platform modules — all using shared brand primitives from UI.jsx
 */

import { useState, useEffect } from "react";
import { api, Spinner } from "../utils/api";
import { HeroHeader, StatRow, Section, GradientAvatar, ScoreBar, EmptyState, TierBadge, IconChip, RowItem } from "../components/UI";

/* ─── INTEL FEED ───────────────────────────────────────────── */
export function IntelFeed() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/api/intel/feed').catch(() => []).then(d => { setItems(d || []); setLoading(false); });
  }, []);
  if (loading) return <Spinner/>;

  return (
    <>
      <HeroHeader title="Intelligence Feed" subtitle={`${items.length} SIGNALS · UPDATED NIGHTLY 02:30 UTC`} badge="LIVE"/>
      <StatRow items={[
        { label:'Total signals',     val: items.length, cls:'or', icon:'⚡' },
        { label:'High-priority',     val: items.filter(i => (i.score||0) >= 70).length, cls:'gd', icon:'★' },
        { label:'Companies tracked', val: new Set(items.map(i=>i.company_name).filter(Boolean)).size, cls:'tl', icon:'◷' },
        { label:'New today',         val: items.filter(i => i.is_new).length, cls:'bl', icon:'✦' },
      ]}/>
      <Section title="All signals" action="EXPORT →">
        {items.length === 0 ? (
          <EmptyState icon="⚡" title="No signals yet" message="Intel scrape runs nightly. Check back after 02:30 UTC."/>
        ) : items.map(item => (
          <RowItem
            key={item.id}
            left={<IconChip icon="⚡"/>}
            title={item.title}
            subtitle={`${item.time_ago || 'recent'} · ${item.company_name || 'unknown'}`}
            right={<ScoreBar score={item.score || 0}/>}
          />
        ))}
      </Section>
    </>
  );
}

/* ─── JOB SCOUT ────────────────────────────────────────────── */
export function JobScout() {
  return (
    <>
      <HeroHeader title="Job Scout" subtitle="MENA HIRING SIGNALS · DAILY DIGEST 07:30 GST" badge="WEEK 8"/>
      <EmptyState icon="⊕" title="Activating in Week 8" message="Job Scout will start scraping MENA job boards once Playwright dependencies are installed on the production server."/>
    </>
  );
}

/* ─── ATS PIPELINE ─────────────────────────────────────────── */
export function ATSPipeline() {
  const [pipeline, setPipeline] = useState({});
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    Promise.all([
      api.get('/api/ats/pipeline').catch(() => ({})),
      api.get('/api/ats/candidates').catch(() => []),
    ]).then(([p, c]) => { setPipeline(p || {}); setCandidates(c || []); setLoading(false); });
  }, []);
  if (loading) return <Spinner/>;

  const stages = ['Sourced','Screened','Interviewing','Offer','Placed','Archived'];
  const total = stages.reduce((a, s) => a + (pipeline[s] || 0), 0);

  return (
    <>
      <HeroHeader title="ATS Pipeline" subtitle={`${total} CANDIDATES IN PIPELINE`} badge="6 STAGES"/>
      <div style={{display:'grid', gridTemplateColumns:'repeat(6, 1fr)', gap:10}}>
        {stages.map((stage, i) => {
          const count = pipeline[stage] || 0;
          const isHot = stage === 'Offer' || stage === 'Placed';
          return (
            <div key={stage} className="card animate-in" style={{
              animationDelay: `${i*0.04}s`,
              padding: '14px 16px',
              borderTop: `2px solid ${isHot ? 'var(--gd)' : 'var(--or)'}`,
            }}>
              <div style={{fontFamily:'var(--mono)', fontSize:9, color:'#999', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:8}}>{stage}</div>
              <div style={{fontSize:24, fontWeight:700, color: isHot ? 'var(--gd)' : '#fff', letterSpacing:'-0.02em'}}>{count}</div>
            </div>
          );
        })}
      </div>
      <Section title="Recent candidates" action="ADD CANDIDATE +">
        {candidates.length === 0 ? (
          <EmptyState icon="◫" title="No candidates yet" message="Add your first candidate to start building the pipeline."/>
        ) : candidates.map(c => (
          <RowItem
            key={c.id}
            left={<GradientAvatar name={c.full_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || '?'}/>}
            title={c.full_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || 'Unknown candidate'}
            subtitle={`${c.current_title || 'unknown role'}${c.current_company ? ' · ' + c.current_company : ''}`}
            right={<TierBadge tier={c.stage || 'Sourced'}/>}
          />
        ))}
      </Section>
    </>
  );
}

/* ─── CRM VIEW ─────────────────────────────────────────────── */
export function CRMView() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/api/crm/companies').catch(() => []).then(d => { setCompanies(d || []); setLoading(false); });
  }, []);
  if (loading) return <Spinner/>;

  return (
    <>
      <HeroHeader title="CRM" subtitle={`${companies.length} COMPANIES · MENA-FOCUSED`} badge="ACTIVE" accent="gold"/>
      <StatRow items={[
        { label:'Companies tracked', val: companies.length, cls:'or', icon:'◷' },
        { label:'Hot leads',         val: companies.filter(c => c.health_status === 'Hot').length, cls:'gd', icon:'★' },
        { label:'Active accounts',   val: companies.filter(c => c.health_status === 'Active').length, cls:'tl', icon:'◎' },
        { label:'Total contacts',    val: companies.reduce((a,c) => a + Number(c.contact_count || 0), 0), cls:'bl', icon:'◫' },
      ]}/>
      <Section title="Companies" action="ADD COMPANY +">
        {companies.length === 0 ? (
          <EmptyState icon="◷" title="No companies yet" message="Add your first company to start tracking relationships."/>
        ) : companies.map(c => (
          <RowItem
            key={c.id}
            left={<GradientAvatar name={c.name}/>}
            title={c.name}
            subtitle={`${c.sector || 'unknown'} · ${c.country || ''}${c.employee_count ? ' · ' + c.employee_count + ' employees' : ''}`}
            right={<TierBadge tier={c.health_status || 'Active'}/>}
          />
        ))}
      </Section>
    </>
  );
}

/* ─── BRIEF MATCHING ──────────────────────────────────────── */
export function BriefMatching() {
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/api/briefs').catch(() => []).then(d => { setBriefs(d || []); setLoading(false); });
  }, []);
  if (loading) return <Spinner/>;

  return (
    <>
      <HeroHeader title="Brief Matching" subtitle="AI-POWERED CANDIDATE-TO-ROLE MATCHING" badge="VOYAGE + CLAUDE"/>
      <StatRow items={[
        { label:'Active briefs', val: briefs.filter(b => b.status === 'Active').length, cls:'or', icon:'◎' },
        { label:'On hold',       val: briefs.filter(b => b.status === 'On hold').length, cls:'gd', icon:'◌' },
        { label:'Filled',        val: briefs.filter(b => b.status === 'Filled').length, cls:'tl', icon:'★' },
        { label:'Total matches', val: briefs.reduce((a,b) => a + (b.match_count || 0), 0), cls:'bl', icon:'◫' },
      ]}/>
      <Section title="All briefs" action="CREATE BRIEF +">
        {briefs.length === 0 ? (
          <EmptyState icon="◎" title="No briefs yet" message="Create a brief to start matching candidates with AI."/>
        ) : briefs.map(b => (
          <RowItem
            key={b.id}
            left={<GradientAvatar name={b.company_name || '?'}/>}
            title={b.title}
            subtitle={`${b.company_name || 'unknown'} · ${b.location || 'remote'} · ${b.seniority || ''}`}
            right={<TierBadge tier={b.status || 'Active'}/>}
          />
        ))}
      </Section>
    </>
  );
}

/* ─── SEQUENCES ────────────────────────────────────────────── */
export function Sequences() {
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/api/sequences').catch(() => []).then(d => { setSequences(d || []); setLoading(false); });
  }, []);
  if (loading) return <Spinner/>;

  return (
    <>
      <HeroHeader title="Sequences" subtitle="AUTOMATED MULTI-STEP OUTREACH · POWERED BY RESEND" badge="EMAIL"/>
      <StatRow items={[
        { label:'Active sequences', val: sequences.filter(s => s.active).length, cls:'or', icon:'◈' },
        { label:'Total enrolled',   val: sequences.reduce((a,s) => a + (s.enrolled || 0), 0), cls:'gd', icon:'◫' },
        { label:'Sent this week',   val: sequences.reduce((a,s) => a + (s.sent_this_week || 0), 0), cls:'tl', icon:'✉' },
        { label:'Reply rate',       val: sequences.length ? `${Math.round(sequences.reduce((a,s) => a + (s.reply_rate || 0), 0) / sequences.length)}%` : '—', cls:'bl', icon:'★' },
      ]}/>
      <Section title="All sequences" action="NEW SEQUENCE +">
        {sequences.length === 0 ? (
          <EmptyState icon="◈" title="No sequences yet" message="Build your first multi-step outreach sequence."/>
        ) : sequences.map(s => (
          <RowItem
            key={s.id}
            left={<IconChip icon="✉"/>}
            title={s.name}
            subtitle={`${s.steps || 0} steps · ${s.enrolled || 0} enrolled · ${s.sent || 0} sent`}
            right={<TierBadge tier={s.active ? 'Active' : 'Pending'}/>}
          />
        ))}
      </Section>
    </>
  );
}

/* ─── INTERVIEWS ──────────────────────────────────────────── */
export function Interviews() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/api/interviews').catch(() => []).then(d => { setSessions(d || []); setLoading(false); });
  }, []);
  if (loading) return <Spinner/>;

  return (
    <>
      <HeroHeader title="Voice Interviews" subtitle="AI-POWERED ASYNCHRONOUS INTERVIEWS · ELEVENLABS + DEEPGRAM" badge="VOICE" accent="teal"/>
      <StatRow items={[
        { label:'Total sessions',  val: sessions.length, cls:'or', icon:'◉' },
        { label:'Completed',       val: sessions.filter(s => s.completed).length, cls:'gd', icon:'★' },
        { label:'In progress',     val: sessions.filter(s => s.in_progress).length, cls:'tl', icon:'◌' },
        { label:'Avg score',       val: sessions.length ? `${Math.round(sessions.reduce((a,s) => a + (s.score || 0), 0) / sessions.length)}` : '—', cls:'bl', icon:'◫' },
      ]}/>
      <Section title="Recent interviews" action="START NEW +">
        {sessions.length === 0 ? (
          <EmptyState icon="◉" title="No interviews yet" message="Voice interviews go live in Week 10."/>
        ) : sessions.map(s => (
          <RowItem
            key={s.id}
            left={<GradientAvatar name={s.candidate_name}/>}
            title={s.candidate_name}
            subtitle={`${s.role_title || 'unknown role'} · ${s.duration_min || '—'} min`}
            right={s.score ? <ScoreBar score={s.score}/> : <TierBadge tier="Pending"/>}
          />
        ))}
      </Section>
    </>
  );
}

/* ─── CLIENT PORTAL ───────────────────────────────────────── */
export function ClientPortal() {
  const [intel, setIntel] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    Promise.all([
      api.get('/api/intel/feed?limit=5').catch(() => []),
      api.get('/api/briefs?status=Active').catch(() => []),
    ]).then(([i, b]) => { setIntel(i || []); setBriefs(b || []); setLoading(false); });
  }, []);
  if (loading) return <Spinner/>;

  return (
    <>
      <HeroHeader title="Client Intelligence Portal" subtitle="MARKET INTEL · ACTIVE BRIEFS · HIRING DATA CURATED FOR YOU" badge="CLIENT VIEW" accent="gold"/>
      <div className="two-col">
        <Section title="Market signals">
          {intel.length === 0 ? <EmptyState icon="⚡" title="No signals yet"/> : intel.map(i => (
            <RowItem key={i.id} left={<IconChip icon="⚡"/>} title={i.title} subtitle={i.time_ago || 'recent'} right={<ScoreBar score={i.score || 0}/>}/>
          ))}
        </Section>
        <Section title="Your active briefs">
          {briefs.length === 0 ? <EmptyState icon="◎" title="No active briefs"/> : briefs.map(b => (
            <RowItem key={b.id} left={<GradientAvatar name={b.company_name}/>} title={b.title} subtitle={b.location || ''} right={<TierBadge tier={b.status}/>}/>
          ))}
        </Section>
      </div>
    </>
  );
}
